import * as React from 'react';
import { useFormModal } from '@repo/form';
import { FormModal } from '@repo/web-form';
import { FormField, Input } from '../../../components/ui/FormField';
import { useToast } from '../../../components/ui/Toast';
import { vaultHooks } from '../network/vault.api';
import { Decimal } from 'decimal.js';
import { Money } from '../../../components/ui/Money';
import { DEFAULT_CURRENCY } from '../../../lib/locale';
import { z } from 'zod';

const schema = z.object({
  amount: z.string().min(1, 'Valid amount is required'),
  notes: z.string().optional(),
});

interface RecordDepositModalProps {
  open: boolean;
  onClose: () => void;
  maxAmountPaise: number;
}

export function RecordDepositModal({ open, onClose, maxAmountPaise }: RecordDepositModalProps) {
  const { toast } = useToast();
  const mutation = vaultHooks.useDeposit();

  const modal = useFormModal(
    { defaultValues: { amount: '', notes: '' }, schema },
    mutation,
    {
      onBeforeSubmit: (value) => {
        const amountPaise = new Decimal(value.amount || 0).mul(100).round().toNumber();
        if (amountPaise <= 0) {
          throw new Error('Amount must be greater than zero');
        }
        if (amountPaise > maxAmountPaise) {
          throw new Error('Cannot deposit more than current vault balance');
        }
        return { amountPaise, notes: value.notes || undefined };
      },
      onSuccess: () => {
        toast({ type: 'success', title: 'Deposit Recorded', description: 'Bank deposit recorded successfully.' });
        onClose();
      },
      onError: (e) => {
        toast({ type: 'error', title: 'Error', description: e.message ?? 'Failed to record deposit' });
      },
    },
  );

  React.useEffect(() => {
    if (open) {
      modal.form.reset();
      modal.form.setFieldValue('amount', String(maxAmountPaise / 100));
    }
  }, [open, maxAmountPaise]);

  return (
    <FormModal open={open} onClose={onClose} title="Record Bank Deposit" modal={modal} submitLabel="Record Deposit">
      <div className="bg-surface-2 p-3 rounded-md mb-4 text-sm">
        <div className="text-text-secondary">Available Vault Balance</div>
        <div className="font-mono text-lg text-text-primary"><Money paise={maxAmountPaise} /></div>
      </div>

      <modal.form.Field
        name="amount"
        validators={{
          onChange: ({ value }: { value: any }) => {
            if (!value) return 'Valid amount is required';
            const isValid = /^\d+(\.\d{1,2})?$/.test(value) && value !== '0' && value !== '0.00';
            return isValid ? undefined : 'Valid amount is required';
          },
        }}
        children={(field: any) => (
          <FormField
            label="Amount to Deposit"
            required
            error={modal.fieldError(field.state.meta)}
          >
            <Input
              type="number"
              step="0.01"
              min="0.01"
              max={maxAmountPaise / 100}
              placeholder="0.00"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              startIcon={<span className="text-text-muted">{DEFAULT_CURRENCY}</span>}
            />
          </FormField>
        )}
      />

      <modal.form.Field
        name="notes"
        children={(field: any) => (
          <FormField label="Bank Reference / Notes" required>
            <Input
              placeholder="e.g. Deposit slip 12345"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
            />
          </FormField>
        )}
      />
    </FormModal>
  );
}
